import React, { useState } from 'react';
import { X, Calendar, Clock, BookOpen, FileText, Video, PenLine } from 'lucide-react';
import { DERS_RENKLERI } from '../contexts/ProgramContext';
interface ScheduleItemData {
  title: string;
  type: 'resource' | 'test' | 'video' | 'homework';
  subject: string;
  resourceId?: number;
  testId?: number;
  videoId?: number;
  homeworkId?: number;
  description?: string;
}
interface ScheduleModalProps {
  initialData: ScheduleItemData;
  onClose: () => void;
  onSave: (data: {
    title: string;
    subject: string;
    type: string;
    day: string;
    startTime: string;
    endTime: string;
    notes?: string;
  }) => void;
}
export function ScheduleModal({
  initialData,
  onClose,
  onSave
}: ScheduleModalProps) {
  const [day, setDay] = useState<string>('Pazartesi');
  const [startTime, setStartTime] = useState<string>('15:00');
  const [endTime, setEndTime] = useState<string>('16:00');
  const [notes, setNotes] = useState<string>(initialData.description || '');
  // Available days for selection
  const days = ['Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi', 'Pazar'];
  // Available time slots for selection
  const timeSlots = Array.from({
    length: 24
  }, (_, i) => {
    const hour = i.toString().padStart(2, '0');
    return `${hour}:00`;
  });
  // Get gradient color based on subject
  const gradientClass = DERS_RENKLERI[initialData.subject.toUpperCase()] || 'from-purple-600 to-indigo-600';
  // Map content type to activity type - Ensure correct mapping
  const getActivityType = (type: string) => {
    switch (type) {
      case 'homework':
        return 'homework';
      // Ödev Yapma
      case 'video':
        return 'video';
      // Video İzleme
      case 'resource':
        return 'resource';
      // Kaynak Çalışması
      case 'test':
        return 'test';
      // Test Çözümü
      default:
        return 'study';
      // Konu Çalışma (default)
    }
  };
  // Get icon based on content type
  const getContentIcon = () => {
    switch (initialData.type) {
      case 'resource':
        return <BookOpen className="w-5 h-5 text-white" data-id="element-952" />;
      case 'test':
        return <PenLine className="w-5 h-5 text-white" data-id="element-953" />;
      case 'video':
        return <Video className="w-5 h-5 text-white" data-id="element-954" />;
      case 'homework':
        return <FileText className="w-5 h-5 text-white" data-id="element-955" />;
      default:
        return <BookOpen className="w-5 h-5 text-white" data-id="element-956" />;
    }
  };
  // Get formatted activity title based on the type
  const getFormattedTitle = () => {
    switch (initialData.type) {
      case 'homework':
        // Remove any existing prefix to avoid duplication
        const homeworkTitle = initialData.title.replace(/^Ödev Yapma: |^Ödev: /, '');
        return `Ödev: ${homeworkTitle}`;
      case 'video':
        // Remove any existing prefix to avoid duplication
        const videoTitle = initialData.title.replace(/^Video İzleme: |^Video: /, '');
        return `Video: ${videoTitle}`;
      case 'resource':
        // Remove any existing prefix to avoid duplication
        const resourceTitle = initialData.title.replace(/^Kaynak Çalışması: |^Kaynak: /, '');
        return `Kaynak: ${resourceTitle}`;
      case 'test':
        // Remove any existing prefix to avoid duplication
        const testTitle = initialData.title.replace(/^Test Çözümü: |^Test: /, '');
        return `Test: ${testTitle}`;
      default:
        return initialData.title;
    }
  };
  // Auto-generate notes based on activity type if notes are empty
  const getAutoNotes = () => {
    if (notes.trim()) return notes;
    // If notes are empty, generate based on title and type
    const title = initialData.title.replace(/^Video İzleme: |^Kaynak Çalışması: |^Test Çözümü: |^Ödev Yapma: |^Video: |^Kaynak: |^Test: |^Ödev: /, '');
    switch (initialData.type) {
      case 'video':
        return `"${title}" videosunu izleme`;
      case 'resource':
        return `"${title}" kaynağını çalışma`;
      case 'test':
        return `"${title}" testini çözme`;
      case 'homework':
        return `"${title}" ödevini yapma`;
      default:
        return '';
    }
  };
  const handleSubmit = () => {
    onSave({
      title: getFormattedTitle(),
      subject: initialData.subject,
      type: getActivityType(initialData.type),
      day,
      startTime,
      endTime,
      notes: getAutoNotes()
    });
    onClose(); // Explicitly close the modal after saving
  };
  return <div className="fixed inset-0 z-50 overflow-y-auto bg-black/75 flex items-center justify-center p-4" data-id="element-957">
      <div className="relative p-[1px] rounded-xl overflow-hidden bg-gradient-to-r from-purple-600 to-indigo-600 w-full max-w-md" data-id="element-958">
        <div className="relative bg-black/90 rounded-xl" data-id="element-959">
          <div className="p-4 border-b border-gray-800 flex justify-between items-center" data-id="element-960">
            <h2 className="text-xl font-bold" data-id="element-961">Programa Ekle</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-white" data-id="element-962">
              <X className="w-6 h-6" data-id="element-963" />
            </button>
          </div>
          <div className="p-6 space-y-4" data-id="element-964">
            <div className="relative p-[1px] rounded-lg overflow-hidden bg-gradient-to-r from-purple-600 to-indigo-600" data-id="element-965">
              <div className="bg-black/90 p-4 rounded-lg" data-id="element-966">
                <div className="flex items-center gap-3" data-id="element-967">
                  <div className={`p-2 rounded-lg bg-gradient-to-r ${gradientClass}`} data-id="element-968">
                    {getContentIcon()}
                  </div>
                  <div data-id="element-969">
                    <h3 className="font-medium" data-id="element-970">{getFormattedTitle()}</h3>
                    <p className="text-sm text-gray-400" data-id="element-971">
                      {initialData.subject}
                    </p>
                    <p className="text-xs text-purple-400 mt-1" data-id="element-972">
                      {initialData.type === 'homework' && 'Ödev Yapma'}
                      {initialData.type === 'video' && 'Video İzleme'}
                      {initialData.type === 'resource' && 'Kaynak Çalışması'}
                      {initialData.type === 'test' && 'Test Çözümü'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="space-y-2" data-id="element-973">
              <label className="text-sm font-medium text-gray-300" data-id="element-974">Gün</label>
              <select value={day} onChange={e => setDay(e.target.value)} className="w-full bg-white/5 text-white rounded-lg px-4 py-3" style={{
              WebkitAppearance: 'none',
              MozAppearance: 'none',
              appearance: 'none',
              backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e")`,
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'right 1rem center',
              backgroundSize: '1em'
            }} data-id="element-975">
                {days.map(d => <option key={d} value={d} data-id="element-976">
                    {d}
                  </option>)}
              </select>
              <style jsx global data-id="element-977">{`
                select option {
                  background-color: #1a1a1a;
                  color: white;
                }
                select option:hover,
                select option:focus,
                select option:active,
                select option:checked {
                  background-color: #6d28d9 !important;
                  color: white !important;
                }
              `}</style>
            </div>
            <div className="grid grid-cols-2 gap-4" data-id="element-978">
              <div className="space-y-2" data-id="element-979">
                <label className="text-sm font-medium text-gray-300" data-id="element-980">
                  Başlangıç Saati
                </label>
                <select value={startTime} onChange={e => setStartTime(e.target.value)} className="w-full bg-white/5 text-white rounded-lg px-4 py-3" style={{
                WebkitAppearance: 'none',
                MozAppearance: 'none',
                appearance: 'none',
                backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 0.5rem center',
                backgroundSize: '1em'
              }} data-id="element-981">
                  {timeSlots.map(time => <option key={time} value={time} data-id="element-982">
                      {time}
                    </option>)}
                </select>
              </div>
              <div className="space-y-2" data-id="element-983">
                <label className="text-sm font-medium text-gray-300" data-id="element-984">
                  Bitiş Saati
                </label>
                <select value={endTime} onChange={e => setEndTime(e.target.value)} className="w-full bg-white/5 text-white rounded-lg px-4 py-3" style={{
                WebkitAppearance: 'none',
                MozAppearance: 'none',
                appearance: 'none',
                backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 0.5rem center',
                backgroundSize: '1em'
              }} data-id="element-985">
                  {timeSlots.map(time => <option key={time} value={time} data-id="element-986">
                      {time}
                    </option>)}
                </select>
              </div>
            </div>
            <div className="space-y-2" data-id="element-987">
              <label className="text-sm font-medium text-gray-300" data-id="element-988">
                Notlar
              </label>
              <textarea value={notes} onChange={e => setNotes(e.target.value)} className="w-full bg-white/5 text-white rounded-lg px-4 py-3 min-h-[100px]" placeholder={getAutoNotes()} data-id="element-989" />
            </div>
          </div>
          <div className="p-4 border-t border-gray-800 flex justify-end gap-3" data-id="element-990">
            <button onClick={onClose} className="px-4 py-2 bg-transparent border border-gray-700 hover:bg-white/5 text-gray-300 rounded-lg transition-colors" data-id="element-991">
              İptal
            </button>
            <button onClick={handleSubmit} className="px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg hover:from-purple-700 hover:to-indigo-700 transition-colors" data-id="element-992">
              Programa Ekle
            </button>
          </div>
        </div>
      </div>
    </div>;
}